/**
 * SSH Key management page
 */

import React, { useState, useEffect } from 'react';
import { SSHKey, SSHKeyCreate } from '../types';
import { userService } from '../services';
import { validateSSHKey, validateSSHKeyName, formatDateTime } from '../utils';
import { 
  Card, 
  Button, 
  Input, 
  Modal, 
  ModalBody, 
  ModalFooter, 
  Alert, 
  LoadingCard 
} from '../components/ui';

export function SSHKeys() {
  const [sshKeys, setSshKeys] = useState<SSHKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  const [newKey, setNewKey] = useState<SSHKeyCreate>({
    name: '',
    public_key: '',
  });
  const [validationErrors, setValidationErrors] = useState<{
    name?: string;
    public_key?: string;
  }>({});

  useEffect(() => {
    fetchSSHKeys();
  }, []);

  const fetchSSHKeys = async () => {
    try {
      setLoading(true);
      const keys = await userService.getSSHKeys();
      setSshKeys(keys);
      setError('');
    } catch (error: any) {
      setError(error.message || 'Failed to load SSH keys');
    } finally {
      setLoading(false);
    }
  };

  const handleAddKey = async () => {
    // Validate form
    const errors: typeof validationErrors = {};
    
    const nameValidation = validateSSHKeyName(newKey.name);
    if (!nameValidation.valid) {
      errors.name = nameValidation.error;
    }

    const keyValidation = validateSSHKey(newKey.public_key);
    if (!keyValidation.valid) {
      errors.public_key = keyValidation.error;
    }

    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    try {
      setSubmitting(true);
      const createdKey = await userService.createSSHKey(newKey);
      setSshKeys(prev => [...prev, createdKey]);
      setShowAddModal(false);
      setNewKey({ name: '', public_key: '' });
      setValidationErrors({});
    } catch (error: any) {
      setError(error.message || 'Failed to add SSH key');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteKey = async (keyId: number) => {
    try {
      setSubmitting(true);
      await userService.deleteSSHKey(keyId);
      setSshKeys(prev => prev.filter(key => key.id !== keyId));
      setDeleteConfirm(null);
    } catch (error: any) {
      setError(error.message || 'Failed to delete SSH key');
    } finally {
      setSubmitting(false);
    }
  };

  const truncateKey = (key: string, length: number = 50) => {
    if (key.length <= length) return key;
    return key.substring(0, length) + '...';
  };

  if (loading) {
    return (
      <div className="p-8 bg-gray-900 min-h-screen">
        <LoadingCard message="Loading SSH keys..." />
      </div>
    );
  }

  return (
    <div className="p-8 bg-gray-900 min-h-screen">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">SSH Key Management</h1>
          <p className="text-gray-400">
            Manage your SSH public keys for lab authentication. Keys are automatically 
            injected into lab VMs for secure access.
          </p>
        </div>

        {error && (
          <Alert variant="error" className="mb-6" onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-white">Your SSH Keys</h2>
          <Button onClick={() => setShowAddModal(true)}>
            Add SSH Key
          </Button>
        </div>

        {sshKeys.length === 0 ? (
          <Card>
            <div className="text-center py-8">
              <div className="text-gray-400 mb-4">
                <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-6 6c-1.098 0-2.1-.402-2.815-1.061L12 14l4-4zM2 18h1.973c.104 0 .207-.027.297-.079l4.19-2.45a2 2 0 000-3.442L4.27 9.579C4.18 9.527 4.077 9.5 3.973 9.5H2c-.552 0-1 .448-1 1v6c0 .552.448 1 1 1z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-white mb-2">No SSH Keys</h3>
              <p className="text-gray-400 mb-4">
                You haven't added any SSH keys yet. Add your first key to start launching labs.
              </p>
              <Button onClick={() => setShowAddModal(true)}>
                Add Your First SSH Key
              </Button>
            </div>
          </Card>
        ) : (
          <div className="space-y-4">
            {sshKeys.map((key) => (
              <Card key={key.id}>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-medium text-white">{key.name}</h3>
                      <span className="px-2 py-1 text-xs bg-blue-900 text-blue-200 rounded uppercase">
                        {key.key_type}
                      </span>
                    </div>
                    <p className="text-sm text-gray-400 mb-2">
                      {truncateKey(key.public_key)}
                    </p>
                    <div className="text-xs text-gray-500">
                      <div>Fingerprint: {key.fingerprint}</div>
                      <div>Added: {formatDateTime(key.created_at)}</div>
                    </div>
                  </div>
                  <div className="ml-4">
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => setDeleteConfirm(key.id)}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Add SSH Key Modal */}
        <Modal
          isOpen={showAddModal}
          onClose={() => {
            setShowAddModal(false);
            setNewKey({ name: '', public_key: '' });
            setValidationErrors({});
          }}
          title="Add SSH Key"
          size="lg"
        >
          <ModalBody>
            <div className="space-y-4">
              <Input
                label="Key Name"
                placeholder="My SSH Key"
                value={newKey.name}
                onChange={(e) => setNewKey(prev => ({ ...prev, name: e.target.value }))}
                error={validationErrors.name}
                helpText="A descriptive name for this SSH key"
              />
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Public Key
                </label>
                <textarea
                  className="block w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows={4}
                  placeholder="ssh-rsa AAAAB3NzaC1yc2E... or ssh-ed25519 AAAAC3NzaC1lZDI1NTE5..."
                  value={newKey.public_key}
                  onChange={(e) => setNewKey(prev => ({ ...prev, public_key: e.target.value }))}
                />
                {validationErrors.public_key && (
                  <p className="text-sm text-red-400 mt-1">{validationErrors.public_key}</p>
                )}
                <p className="text-sm text-gray-500 mt-1">
                  Paste your SSH public key here. Supports RSA, Ed25519, and ECDSA keys.
                </p>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="secondary"
              onClick={() => {
                setShowAddModal(false);
                setNewKey({ name: '', public_key: '' });
                setValidationErrors({});
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddKey}
              loading={submitting}
            >
              Add Key
            </Button>
          </ModalFooter>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          isOpen={deleteConfirm !== null}
          onClose={() => setDeleteConfirm(null)}
          title="Confirm Deletion"
        >
          <ModalBody>
            <p className="text-gray-300">
              Are you sure you want to delete this SSH key? This action cannot be undone.
              You will lose access to any active labs using this key.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="secondary"
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={submitting}
              onClick={() => deleteConfirm && handleDeleteKey(deleteConfirm)}
            >
              Delete Key
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </div>
  );
}